import React, { useContext, useMemo } from "react";
import { ScrollView, StyleSheet, View } from "react-native";
import { useTheme } from "react-native-elements";
import { Gesture, GestureDetector } from "react-native-gesture-handler";
import { hasFlag } from "../backend/Flags";
import { PlayerContext } from "../backend/PlayerContext";
import { Resource, getResourceCap } from "../backend/Resources";
import { getActionsForAreaByCategory } from "../backend/actions/Actions";
import { BuildingActionBase } from "../backend/buildings/Building";
import { getFirstHint } from "../backend/hints/Hints";
import ActionsContainer from "./utility/ActionsContainer";
import CategoryContainer from "./utility/CategoryContainer";
import { useGestureHandler } from "./utility/Miscellaneous";
import { ProgressBar } from "./utility/ProgressBar";
import { Text } from "./utility/Text";

export default function Campus(props: {
  onSwipeLeft: () => void;
  onSwipeRight: () => void;
}) {
  const playerContext = useContext(PlayerContext);
  const currentHint = getFirstHint(playerContext);
  const hintProgress = currentHint?.progress?.(playerContext);

  const swipeHandlerGesture = useGestureHandler(
    props.onSwipeLeft,
    props.onSwipeRight,
  );
  const nativeGesture = useMemo(() => Gesture.Native(), []);
  const compositeGesture = useMemo(
    () => Gesture.Simultaneous(swipeHandlerGesture, nativeGesture),
    [swipeHandlerGesture, nativeGesture],
  );

  const swipeHandlerGestureBottom = useGestureHandler(
    props.onSwipeLeft,
    props.onSwipeRight,
  );
  const nativeGestureBottom = useMemo(() => Gesture.Native(), []);
  const compositeGestureBottom = useMemo(
    () => Gesture.Simultaneous(swipeHandlerGestureBottom, nativeGestureBottom),
    [swipeHandlerGestureBottom, nativeGestureBottom],
  );

  const allActionsMaxedOut = !Object.values(
    getActionsForAreaByCategory("Campus"),
  )
    .map((actionRecord) => Object.values(actionRecord))
    .flat()
    .some((action) => {
      if (
        !(action instanceof BuildingActionBase) ||
        !action.isVisible(playerContext)
      ) {
        return false;
      }
      const cost = action.getCost(playerContext);
      const isFree =
        Object.keys(cost.resources).length === 0 &&
        Object.keys(cost.items).length === 0;
      const capIsEnough = (
        Object.keys(cost.resources) as Array<Resource>
      ).every((resource: Resource) => {
        return (
          (cost.resources[resource] ?? 0) <=
          getResourceCap(playerContext, resource)
        );
      });
      if (capIsEnough) {
        return true;
      }
    });

  const showCampusHint =
    allActionsMaxedOut &&
    (currentHint?.description == "Explore the School Basement" ||
      currentHint?.description == "Explore the Thunder Tower" ||
      currentHint?.description ==
        "Purchase the elemental upgrades and explore the new dungeons") &&
    (!hasFlag(playerContext, "upgrades_unlocked") ||
      !Object.values(getActionsForAreaByCategory("Upgrades"))
        .map((actionRecord) => Object.values(actionRecord))
        .flat()
        .some((action) => {
          return action.isVisible(playerContext);
        }));

  const theme = useTheme();
  const campusHint = useMemo(() => {
    if (showCampusHint) {
      return (
        <View style={styles.campusHintWarning}>
          <Text style={{ color: theme.theme.colors.warning }}>
            It looks like you have built everything you can for the time being.
            Make progress in Exploration to find ways to build more!
          </Text>
        </View>
      );
    }
    return null;
  }, [showCampusHint, theme]);

  return useMemo(
    () => (
      <View style={styles.campusContainer}>
        <GestureDetector key="campusGesture" gesture={compositeGesture}>
          <ScrollView
            contentContainerStyle={styles.contentContainer}
            style={styles.container}
          >
            {campusHint}
            <ActionsContainer area="Campus" useSubcategories={true} />
          </ScrollView>
        </GestureDetector>
        <GestureDetector key="hintGesture" gesture={compositeGestureBottom}>
          <View
            style={[
              styles.hintContainer,
              { borderColor: theme.theme.colors.divider },
            ]}
          >
            <CategoryContainer
              titleStyle={styles.categoryTitle}
              title="Current Goal"
              containerStyle={styles.categoryContainer}
            >
              <View style={styles.hintContents}>
                <Text style={styles.hintContentsText}>
                  {currentHint?.description}!
                </Text>
                {currentHint?.progress && (
                  <ProgressBar
                    progress={hintProgress}
                    width={null}
                    useNativeDriver={true}
                    style={styles.hintProgress}
                  />
                )}
              </View>
            </CategoryContainer>
          </View>
        </GestureDetector>
      </View>
    ),
    [
      currentHint,
      campusHint,
      hintProgress,
      compositeGesture,
      compositeGestureBottom,
      theme,
    ],
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    width: "100%",
  },
  contentContainer: {
    alignItems: "stretch",
    justifyContent: "flex-start",
    width: "100%",
    paddingHorizontal: 8,
  },
  hintContents: {
    marginLeft: 16,
    marginTop: 8,
    paddingRight: 32,
  },
  hintContentsText: {
    fontStyle: "italic",
  },
  hintProgress: {
    marginTop: 4,
  },
  hintContainer: {
    borderTopWidth: 1,
  },
  categoryTitle: {
    marginLeft: 8,
  },
  categoryContainer: {
    paddingTop: 4,
    marginBottom: 8,
  },
  campusContainer: {
    height: "100%",
    alignItems: "stretch",
    justifyContent: "flex-start",
    width: "100%",
  },
  campusHintWarning: {
    marginTop: 16,
  },
});
